import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title = "MisTramites.mx - Trámites Gubernamentales en México",
  description = "Gestiona tus trámites gubernamentales en México de forma rápida, segura y profesional. RFC, Pasaporte, INE, Actas y más. Seguimiento 24/7.",
  keywords = "trámites méxico, trámites gubernamentales, RFC, pasaporte, INE",
  image = "https://mistramites.mx/og-image.jpg",
  type = "website"
}) => {
  const location = useLocation();
  const currentUrl = `https://mistramites.mx${location.pathname}`;

  useEffect(() => {
    document.title = title;

    const updateMetaTag = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`) || 
                    document.querySelector(`meta[name="${property}"]`);
      
      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          element.setAttribute('property', property);
        } else {
          element.setAttribute('name', property);
        }
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };

    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', currentUrl);
    updateMetaTag('og:image', image);
    updateMetaTag('og:type', type);
    
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:url', currentUrl);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', currentUrl);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', currentUrl);
      document.head.appendChild(canonical);
    }
  }, [title, description, keywords, image, type, currentUrl]);

  return null;
};

// SEO configs for static pages
export const seoConfigs = {
  home: {
    title: "MisTramites.mx - Trámites Gubernamentales Rápidos y Seguros",
    description: "Gestiona tus trámites gubernamentales en México con la plataforma líder. RFC, Pasaporte, INE, Actas y más. 95% de éxito. Seguimiento 24/7. ¡Cotización gratuita!",
    keywords: "trámites méxico, trámites gubernamentales, RFC, pasaporte, INE, actas de nacimiento, gestión de trámites, trámites rápidos"
  },
  categories: {
    title: "Todas las Categorías de Trámites en México | MisTramites.mx",
    description: "Explora las 17 categorías de trámites disponibles en México. Más de 60 trámites especializados: vehiculares, fiscales, migratorios, empresariales y más. Cotización gratuita.",
    keywords: "categorías trámites, tipos de trámites méxico, servicios gubernamentales, trámites en línea"
  },
  login: {
    title: "Iniciar Sesión - MisTramites.mx",
    description: "Accede a tu cuenta de MisTramites.mx para gestionar y dar seguimiento a tus trámites gubernamentales en México.",
    keywords: "login mistramites, acceder cuenta, iniciar sesión trámites"
  },
  register: {
    title: "Crear Cuenta - MisTramites.mx",
    description: "Crea tu cuenta en MisTramites.mx y comienza a gestionar tus trámites gubernamentales de forma profesional y segura.",
    keywords: "registro mistramites, crear cuenta, nuevos usuarios trámites"
  },
  terms: {
    title: "Términos y Condiciones - MisTramites.mx",
    description: "Consulta los términos y condiciones de uso de la plataforma MisTramites.mx para la gestión de trámites gubernamentales.",
    keywords: "términos condiciones, aviso legal, mistramites"
  },
  privacy: {
    title: "Aviso de Privacidad - MisTramites.mx",
    description: "Conoce cómo protegemos tus datos personales. Aviso de privacidad de MisTramites.mx conforme a la Ley Federal de Protección de Datos.",
    keywords: "aviso privacidad, protección datos, LFPDPPP, mistramites"
  },
  dashboard: {
    title: "Mi Panel de Control - MisTramites.mx",
    description: "Panel de control para gestionar y dar seguimiento a todos tus trámites gubernamentales en México.",
    keywords: "dashboard trámites, panel control, seguimiento trámites"
  }
};

/**
 * Genera SEO dinámico para una categoría basándose en datos reales del API.
 * Se actualiza automáticamente cuando se agregan/quitan/modifican trámites.
 * 
 * @param {Object} category - Datos de la categoría del API (name, description, application_type_count)
 * @param {Array} tramites - Lista de trámites del API (name, description)
 * @returns {Object} { title, description, keywords }
 */
export const buildCategorySeo = (category, tramites = []) => {
  if (!category) return seoConfigs.home;

  const name = category.name;
  const count = tramites.length || category.application_type_count || 0;

  // Build tramite names list for description and keywords
  const tramiteNames = tramites.map(t => t.name);
  const tramiteList = tramiteNames.slice(0, 5).join(', ');
  const extraCount = tramiteNames.length > 5 ? ` y ${tramiteNames.length - 5} más` : '';

  // Title: "Trámites Vehiculares en México (9) | MisTramites.mx"
  const title = `Trámites de ${name} en México (${count}) | MisTramites.mx`;

  // Description: uses real tramite names so they get indexed
  let description;
  if (tramiteNames.length > 0) {
    description = `${count} trámites de ${name}: ${tramiteList}${extraCount}. Gestión profesional en México con seguimiento 24/7. ¡Cotización gratuita!`;
  } else {
    description = category.description
      ? `Trámites de ${name} en México: ${category.description}. Gestión profesional con seguimiento 24/7. ¡Cotización gratuita!`
      : `Gestiona tus trámites de ${name} en México de forma rápida y segura. Cotización gratuita y seguimiento 24/7.`;
  }

  // Truncate description to ~155 chars for Google
  if (description.length > 160) {
    description = description.substring(0, 157) + '...';
  }

  // Keywords: category name + each tramite name (captures geo-specific searches)
  const baseKeywords = [`trámites ${name.toLowerCase()}`, `${name.toLowerCase()} méxico`];
  const tramiteKeywords = tramiteNames.map(n => n.toLowerCase());
  const keywords = [...baseKeywords, ...tramiteKeywords].join(', ');

  return { title, description, keywords };
};

/**
 * Genera SEO dinámico para la página general de categorías
 * usando la lista real de categorías del API.
 * 
 * @param {Array} categories - Lista de categorías del API
 * @returns {Object} { title, description, keywords }
 */
export const buildCategoriesListSeo = (categories = []) => {
  if (!categories.length) return seoConfigs.categories;

  const count = categories.length;
  const totalTramites = categories.reduce((sum, c) => sum + (c.application_type_count || 0), 0);
  const categoryNames = categories.slice(0, 6).map(c => c.name).join(', ');

  return {
    title: `${count} Categorías de Trámites en México | MisTramites.mx`,
    description: `Explora ${count} categorías con ${totalTramites}+ trámites: ${categoryNames} y más. Gestión profesional en México. ¡Cotización gratuita!`.substring(0, 160),
    keywords: categories.map(c => `trámites ${c.name.toLowerCase()}`).join(', ')
  };
};

export default SEO;
